import {
  Flex,
  Image,
  Button,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Portal,
  Text,
  useToast,
  useDisclosure,
  Box,
  HStack,
  Icon,
  Badge,
} from "@chakra-ui/react";
import { IoChevronDown } from "react-icons/io5";
import { IoCalendarOutline, IoTicketOutline, IoStorefrontOutline, IoAddCircleOutline, IoPersonAddOutline, IoPersonOutline, IoBriefcaseOutline } from "react-icons/io5";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebase-config";
import logo from "../../Assets/Images/logo.png";
import { BecomeHostModal, CreateEventModal, SignInModal, MyTicketsModal, EditProfileModal, HostSettingsModal } from "../Modals";
import { ProfileTasks } from "./ProfileTasks";

const hasRealPhoto = (value) => {
  const photo = String(value || "").trim();
  return Boolean(photo) && photo !== "null" && photo !== "undefined";
};

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useAuthState(auth);
  const [isHost, setIsHost] = useState(false);
  // Venue partners are a separate role — you can list a venue without ever
  // becoming an event host.
  const [isVenueOwner, setIsVenueOwner] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [hasBio, setHasBio] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [hasInterests, setHasInterests] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [profileFocus, setProfileFocus] = useState(null);
  // Tracks whether the user has scrolled past the very top so we can
  // morph the navbar from a flat white bar into a frosted-glass pill.
  const [scrolled, setScrolled] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { isOpen: isHostModalOpen, onOpen: onHostModalOpen, onClose: onHostModalClose } = useDisclosure();
  const { isOpen: isEventModalOpen, onOpen: onEventModalOpen, onClose: onEventModalClose } = useDisclosure();
  const { isOpen: isSignInModalOpen, onOpen: onSignInModalOpen, onClose: onSignInModalClose } = useDisclosure();
  const { isOpen: isMyTicketsOpen, onOpen: onMyTicketsOpen, onClose: onMyTicketsClose } = useDisclosure();
  const { isOpen: isEditProfileOpen, onOpen: onEditProfileOpen, onClose: onEditProfileClose } = useDisclosure();
  const { isOpen: isHostSettingsOpen, onOpen: onHostSettingsOpen, onClose: onHostSettingsClose } = useDisclosure();

  // Host role, venue role, and the profile tasks share one user-doc read.
  const fetchProfile = useCallback(async () => {
    if (!user) {
      setIsHost(false);
      setIsVenueOwner(false);
      setHasPhoto(false);
      setPhotoUrl("");
      setHasBio(false);
      setHasInterests(false);
      setProfileReady(false);
      return;
    }
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const data = userDoc.exists() ? userDoc.data() : {};
      setIsHost(data.isHost || false);
      setIsVenueOwner(data.isVenueOwner || false);
      const photo = [data.avatarUrl, user.photoURL].find(hasRealPhoto) || "";
      setPhotoUrl(photo);
      setHasPhoto(Boolean(photo));
      setHasBio(Boolean(String(data.bio || "").trim()));
      setHasInterests(Array.isArray(data.interests) && data.interests.length > 0);
      setProfileReady(true);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  }, [user]);

  // Refresh when the account changes or they move to another page.
  useEffect(() => {
    fetchProfile();
  }, [user, location.pathname, fetchProfile]);

  const tasksLeft = profileReady ? Number(!hasPhoto) + Number(!hasBio) + Number(!hasInterests) : 0;

  const openProfile = (section) => {
    setProfileFocus(section || null);
    onEditProfileOpen();
  };

  const closeProfile = () => {
    setProfileFocus(null);
    onEditProfileClose();
  };

  const handleSignInClick = () => {
    onSignInModalOpen();
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      toast({
        title: "Signed out successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/");
    } catch (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Flex
        as="header"
        w="100%"
        px={{ base: "12px", md: "20px" }}
        py={scrolled ? "10px" : "15px"}
        gap={2}
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
        top={0}
        zIndex={100}
        bg={scrolled ? "whiteAlpha.800" : "white"}
        backdropFilter={scrolled ? "blur(14px) saturate(180%)" : "none"}
        sx={{
          WebkitBackdropFilter: scrolled
            ? "blur(14px) saturate(180%)"
            : "none",
        }}
        borderBottom="1px solid"
        borderColor={scrolled ? "blackAlpha.100" : "transparent"}
        boxShadow={
          scrolled ? "0 4px 20px -8px rgba(15,23,42,0.12)" : "none"
        }
        transition="background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, padding 0.25s ease"
      >
        <Button
          variant="ghost"
          p={0}
          cursor="pointer"
          flexShrink={1}
          minW={0}
          _hover={{
            transform: "scale(1.02)"
          }}
          transition="all 0.2s"
          height="auto"
          onClick={() => navigate("/")}
        >
          <Image
            src={logo}
            alt="Logo"
            h={scrolled ? { base: "24px", md: "26px" } : { base: "26px", md: "30px" }}
            w={{ base: "120px", md: "140px" }}
            objectFit="contain"
            transition="height 0.25s ease"
          />
        </Button>

        {user ? (
          <Menu placement="bottom-end" autoSelect={false} gutter={10}>
            <MenuButton
              as={Button}
              aria-label={
                tasksLeft
                  ? `Account menu, ${tasksLeft} profile ${tasksLeft === 1 ? "step" : "steps"} left`
                  : "Account menu"
              }
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="full"
              px={2}
              py={1}
              cursor="pointer"
              flexShrink={0}
              _hover={{
                borderColor: "#508cf7",
                transform: "scale(1.02)",
                boxShadow: "0 0 0 1px #508cf7"
              }}
              _active={{
                borderColor: "#508cf7",
                boxShadow: "0 0 0 1px #508cf7"
              }}
              transition="all 0.2s"
              height="auto"
            >
              <HStack spacing={1}>
                <Box position="relative">
                  <Avatar
                    size="sm"
                    name={user.displayName || user.email}
                    src={photoUrl || user.photoURL || undefined}
                    border="2px solid"
                    borderColor="#508cf7"
                  />
                  {tasksLeft > 0 && (
                    <Flex
                      position="absolute"
                      top="-3px"
                      right="-3px"
                      bg="#E39B2B"
                      color="white"
                      fontSize="9px"
                      fontWeight="700"
                      lineHeight="1"
                      borderRadius="full"
                      minW="16px"
                      h="16px"
                      px="3px"
                      align="center"
                      justify="center"
                      border="2px solid white"
                    >
                      {tasksLeft}
                    </Flex>
                  )}
                </Box>
                <Icon as={IoChevronDown} color="gray.600" boxSize={4} />
              </HStack>
            </MenuButton>
            <Portal>
              <MenuList
              bg="white"
              border="1px solid"
              borderColor="gray.100"
              borderRadius="xl"
              boxShadow="0 8px 30px rgba(0,0,0,0.10)"
              py={2}
              minW={{ base: "260px", md: "280px" }}
              maxW="calc(100vw - 16px)"
              zIndex={1500}
              overflow="hidden"
            >
              <Box px={4} py={2} textAlign="left" minW={0}>
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  color="gray.800"
                  noOfLines={1}
                >
                  {user.displayName || "User"}
                </Text>
                <Text
                  fontSize="xs"
                  color="gray.400"
                  mt="1px"
                  noOfLines={1}
                  wordBreak="break-all"
                >
                  {user.email}
                </Text>
                {user.phoneNumber && (
                  <Text fontSize="xs" color="gray.400" noOfLines={1}>
                    {user.phoneNumber}
                  </Text>
                )}
              </Box>
              {profileReady && (
                <ProfileTasks
                  hasPhoto={hasPhoto}
                  hasBio={hasBio}
                  hasInterests={hasInterests}
                  onSelect={openProfile}
                />
              )}
              <MenuDivider my={2} borderColor="gray.100" />

              {isHost ? (
                <>
                  <MenuItem
                    onClick={onEventModalOpen}
                    borderRadius="md"
                    mx={2}
                    px={3}
                    py={2}
                    _hover={{ bg: "blue.50" }}
                    icon={<Icon as={IoAddCircleOutline} boxSize={4} color="#508cf7" />}
                  >
                    <Text fontSize="sm" fontWeight="semibold" color="#508cf7">Add an Event</Text>
                  </MenuItem>
                  <MenuItem
                    onClick={() => navigate("/events")}
                    borderRadius="md"
                    mx={2}
                    px={3}
                    py={2}
                    _hover={{ bg: "gray.50" }}
                    icon={<Icon as={IoCalendarOutline} boxSize={4} color="gray.500" />}
                  >
                    <Text fontSize="sm" color="gray.700">My Events</Text>
                  </MenuItem>
                </>
              ) : (
                <MenuItem
                  onClick={onHostModalOpen}
                  borderRadius="md"
                  mx={2}
                  px={3}
                  py={2}
                  _hover={{ bg: "blue.50" }}
                  icon={<Icon as={IoPersonAddOutline} boxSize={4} color="#508cf7" />}
                >
                  <Text fontSize="sm" fontWeight="semibold" color="#508cf7">Become a Host</Text>
                </MenuItem>
              )}

              {/* Venue items — for anyone who's a venue partner or a host */}
              {(isVenueOwner || isHost) && (
                <MenuItem
                  onClick={() => navigate("/venues")}
                  borderRadius="md"
                  mx={2}
                  px={3}
                  py={2}
                  _hover={{ bg: "gray.50" }}
                  icon={<Icon as={IoStorefrontOutline} boxSize={4} color="gray.500" />}
                >
                  <Text fontSize="sm" color="gray.700">My Venues</Text>
                </MenuItem>
              )}
              {!isVenueOwner && (
                <MenuItem
                  onClick={() => navigate("/list-your-venue")}
                  borderRadius="md"
                  mx={2}
                  px={3}
                  py={2}
                  _hover={{ bg: "blue.50" }}
                  icon={<Icon as={IoStorefrontOutline} boxSize={4} color="#508cf7" />}
                >
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.700">List Your Venue</Text>
                    <Badge colorScheme="blue" fontSize="9px" borderRadius="full" px={2}>NEW</Badge>
                  </HStack>
                </MenuItem>
              )}

              <MenuItem
                onClick={onMyTicketsOpen}
                borderRadius="md"
                mx={2}
                px={3}
                py={2}
                _hover={{ bg: "gray.50" }}
                icon={<Icon as={IoTicketOutline} boxSize={4} color="gray.500" />}
              >
                <Text fontSize="sm" color="gray.700">My Tickets</Text>
              </MenuItem>

              <MenuDivider my={2} borderColor="gray.100" />

              <MenuItem
                onClick={() => openProfile(null)}
                borderRadius="md"
                mx={2}
                px={3}
                py={2}
                _hover={{ bg: "gray.50" }}
                icon={<Icon as={IoPersonOutline} boxSize={4} color="gray.500" />}
              >
                <Text fontSize="sm" color="gray.700">Edit Profile</Text>
              </MenuItem>

              {isHost && (
                <MenuItem
                  onClick={onHostSettingsOpen}
                  borderRadius="md"
                  mx={2}
                  px={3}
                  py={2}
                  _hover={{ bg: "gray.50" }}
                  icon={<Icon as={IoBriefcaseOutline} boxSize={4} color="gray.500" />}
                >
                  <Text fontSize="sm" color="gray.700">Host Settings</Text>
                </MenuItem>
              )}

              <MenuDivider my={2} borderColor="gray.100" />
              <MenuItem
                onClick={handleSignOut}
                borderRadius="md"
                mx={2}
                px={3}
                py={2}
                _hover={{ bg: "red.50" }}
              >
                <Text fontSize="sm" color="red.500" fontWeight="medium">Sign Out</Text>
              </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        ) : (
          <HStack spacing={2} flexShrink={0}>
            <Button
              variant="ghost"
              size="sm"
              color="gray.600"
              fontSize="12px"
              fontWeight="600"
              display={{ base: "none", sm: "inline-flex" }}
              leftIcon={<Icon as={IoStorefrontOutline} boxSize={3.5} />}
              _hover={{ bg: "blue.50", color: "#508cf7" }}
              onClick={() => navigate("/list-your-venue")}
            >
              List Your Venue
            </Button>
            <Button
              bg={"#508cf7"}
              size="sm"
              color={"white"}
              fontSize={"12px"}
              _hover={{ bg: "#226cf1" }}
              onClick={handleSignInClick}
            >
              Sign In
            </Button>
          </HStack>
        )}
      </Flex>

      <BecomeHostModal isOpen={isHostModalOpen} onClose={onHostModalClose} user={user} setIsHost={setIsHost} />
      <CreateEventModal isOpen={isEventModalOpen} onClose={onEventModalClose} user={user} />
      <SignInModal isOpen={isSignInModalOpen} onClose={onSignInModalClose} />
      <MyTicketsModal isOpen={isMyTicketsOpen} onClose={onMyTicketsClose} user={user} />
      <EditProfileModal
        isOpen={isEditProfileOpen}
        onClose={closeProfile}
        onSaved={fetchProfile}
        user={user}
        focusSection={profileFocus}
      />
      <HostSettingsModal isOpen={isHostSettingsOpen} onClose={onHostSettingsClose} user={user} />
    </>
  );
};
